import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useContext, useEffect, useRef, useState } from "react";
import { formatDateDDMMYYYY, toProperCase } from "../../../Utils";
import { FiInfo, FiUser } from "react-icons/fi";
import { CREATE_VENDOR_GN, EDIT_VENDOR_GN, VIEW_VENDOR_GN } from "../../../routes";
import { MdAdd, MdModeEditOutline } from "react-icons/md";
import { ClipboardList, Trash2 } from "lucide-react";
import {
  AccessDenied,
  FilterDropdown,
  GenericTable,
  Pagination,
  RoleValidator,
  SearchableDropdown,
  Spinner,
} from "../../../Components/Common";
import {
  getVGNStatusColor,
  VGN_MATERIAL_COLUMNS,
  VGN_MATERIAL_LABELS,
  VGN_STATUS,
  VGN_STATUS_VALUE_LABELS,
} from "./constants";
import { UserContext } from "../../../Context";
import { ROLES } from "../../../Constants/Strings";
import { useVendorGoodsNote } from "../Hooks/useVendorGoodsNote";
import { useWarehouse } from "../Hooks/useWarehouse";

const VendorGoodsNoteList = () => {
  const {
    loading,
    filters,
    setFilters,
    page,
    totalPages,
    rowsPerPage,
    onPageChange,
    onPageSizeChange,
    fetchAllVGN,
    deleteVendorGoodsNote,
  } = useVendorGoodsNote();

  const navigate = useNavigate();
  const [materials, setMaterials] = useState([]);
  const { fetchWarehouses } = useWarehouse();
  const [isStatusFilterOpen, setIsStatusFilterOpen] = useState(false);
  const dropdownRef = useRef(null);
  const { user } = useContext(UserContext);
  const accessedRoles = [
    ROLES.INVENTORY_ADMIN,
    ROLES.INVENTORY_INCHARGE,
    ROLES.BILLING_INCHARGE,
    ROLES.PROCUREMENT_INCHARGE,
    ROLES.PROJECT_ENGINEER,
    ROLES.PROJECT_MANAGER,
  ].includes(user?.role);

  const loadVGNs = async () => {
    const data = await fetchAllVGN();
    if (data) setMaterials(data);
    else setMaterials([]);
  };

  useEffect(() => {
    loadVGNs();
  }, [page, rowsPerPage, filters]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsStatusFilterOpen(false);
      }
    };

    if (isStatusFilterOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isStatusFilterOpen]);

  const handleDeleteMaterial = async (mrrItem) => {
    const data = await deleteVendorGoodsNote(mrrItem.id);
    if (data) {
      setMaterials((prev) => prev.filter((item) => item.id !== mrrItem.id));
    }
  };

  // Render Field Values for Table
  const renderFields = (key, rowData) => {
    switch (key) {
      case "created": {
        const date = rowData.created.split("T")[0];
        return formatDateDDMMYYYY(date);
      }
      case "status":
        return (
          <span
            className={`px-1.5 py-1 rounded-md text-xs font-medium ${getVGNStatusColor(
              rowData.status
            )}`}
          >
            {toProperCase(rowData.status)}
          </span>
        );
      case "actions":
        return (
          <div className="flex items-center gap-4">
            <FiInfo
              title="View Details"
              className="cursor-pointer text-primary hover:text-secondary text-lg"
              onClick={() =>
                navigate(VIEW_VENDOR_GN.replace(":instanceId", rowData.id))
              }
            />

            <RoleValidator allowedRoles={ROLES.PROJECT_ENGINEER}>               
            <button
              disabled={rowData.status !== VGN_STATUS.DRAFT}
              className={`${
                rowData.status === VGN_STATUS.DRAFT
                  ? "text-blue-500"
                  : "text-gray-400 cursor-not-allowed"
              }`}
              onClick={() =>
                navigate(EDIT_VENDOR_GN.replace(":instanceId", rowData.id))
              }
              title={
                rowData.status === VGN_STATUS.DRAFT
                  ? "Edit Details"
                  : `Cannot Edit in ${toProperCase(rowData.status)} state`
              }
            >
              <MdModeEditOutline className="h-4 w-4" />
            </button>
            </RoleValidator>    

            <RoleValidator allowedRoles={ROLES.PROJECT_ENGINEER}>
              <button
                onClick={() => handleDeleteMaterial(rowData)}
                disabled={rowData.status !== VGN_STATUS.DRAFT}
                className={`${
                  rowData.status === VGN_STATUS.DRAFT
                    ? "text-red-500 hover:text-red-700"
                    : "text-gray-400 cursor-not-allowed"
                }`}
                title={
                  rowData.status === VGN_STATUS.DRAFT
                    ? "Remove"
                    : `Cannot delete in ${toProperCase(rowData.status)} state`
                }
              >
                <Trash2 size={18} />
              </button>
            </RoleValidator>
          </div>
        );
      default:
        return rowData[key];
    }
  };

  // Mobile Card View
  const renderMobileCards = () => (
    <ul className="space-y-3">
      {materials.length > 0 ? (
        materials.map((item) => (
          <li
            key={item.id}
            className="p-2 rounded-lg border text-sm flex justify-between shadow-sm"
            onClick={() =>
              navigate(VIEW_VENDOR_GN.replace(":instanceId", item.id))
            }
          >
            <div className="max-w-[60%]">
              <div className="font-semibold text-gray-800 mb-1 truncate">
                Warehouse {item.warehouse_name}
              </div>
              <div className="text-xs text-gray-600 flex items-center gap-1">
                <FiUser size={16} />
                <span className="font-medium"></span> {item.vendor_name}
              </div>
            </div>
            <div className="flex flex-col items-end justify-between gap-2">
              <div className="flex items-center gap-4">
                <button
                  disabled={item.status !== VGN_STATUS.DRAFT}
                  className={`${
                    item.status === VGN_STATUS.DRAFT
                      ? "text-blue-500"
                      : "text-gray-400 cursor-not-allowed"
                  }`}
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(EDIT_VENDOR_GN.replace(":instanceId", item.id));
                  }}
                  title={
                    item.status === VGN_STATUS.DRAFT
                      ? "Edit Details"
                      : `Cannot Edit in ${toProperCase(item.status)} state`
                  }
                >
                  <MdModeEditOutline className="h-4 w-4" />
                </button>

                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteMaterial(item);
                  }}
                  disabled={item.status !== VGN_STATUS.DRAFT}
                  className={`${
                    item.status === VGN_STATUS.DRAFT
                      ? "text-red-500 hover:text-red-700"
                      : "text-gray-400 cursor-not-allowed"
                  }`}
                  title={
                    item.status === VGN_STATUS.DRAFT
                      ? "Remove"
                      : `Cannot delete in ${toProperCase(item.status)} state`
                  }
                >
                  <Trash2 size={14} />
                </button>
              </div>

              <span
                className={`px-1.5 py-1 rounded-md text-xs font-medium ${getVGNStatusColor(
                  item.status
                )}`}
              >
                {toProperCase(item.status)}
              </span>
            </div>
          </li>
        ))
      ) : (
        <div className="w-full h-[280px] flex justify-center items-center text-gray-300 flex-col">
          <ClipboardList size={35} />
          <span>No Material Requests</span>
        </div>
      )}
    </ul>
  );

  if (!accessedRoles) {
    return <AccessDenied />;
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      className="px-4 py-4"
    >
      <div className="flex justify-between items-center mb-4 mt-2 md:mt-0">
        <h2 className="text-xl font-semibold flex items-center gap-2">
          Vendor Goods Notes
        </h2>
        <RoleValidator allowedRoles={ROLES.PROJECT_ENGINEER}>
          <button
            onClick={() => navigate(CREATE_VENDOR_GN)}
            className=" bg-primary text-white text-sm px-3 py-2 rounded-md hover:bg-blue-700 transition-colors hidden md:block"
          >
            + Create VGN
          </button>
        </RoleValidator>

        <RoleValidator allowedRoles={ROLES.PROJECT_ENGINEER}>
          <button
            onClick={() => navigate(CREATE_VENDOR_GN)}
            className="bg-primary text-white px-2 py-1 rounded-md hover:bg-[#bde0fe] max-h-fit block md:hidden"
            aria-label="Add project"
          >
            <MdAdd size={28} />
          </button>
        </RoleValidator>
      </div>
      <div className="bg-white w-full rounded-2xl shadow-md border px-4 py-3 mb-2">
        <div className="flex flex-wrap gap-2">
          <SearchableDropdown
            fetchFunction={(param) =>
              fetchWarehouses({ ...param, is_site: true })
            }
            onSelect={(warehouse) =>
              setFilters({ ...filters, site_warehouse: warehouse })
            }
            valueKey="id"
            labelKey="name"
            placeholder={"Select Warehouse"}
            width="64"
            includeAllOption={true}
          />
          <div
            ref={dropdownRef}
            className="flex justify-center items-center min-w-[120px]"
          >
            <FilterDropdown
              options={VGN_STATUS_VALUE_LABELS}
              value={filters.status || ""}
              onChange={(status) => setFilters({ ...filters, status })}
              isOpen={isStatusFilterOpen}
              onToggle={() => setIsStatusFilterOpen(!isStatusFilterOpen)}
              label={"Status"}
            />
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-[60vh]">
          <Spinner />
        </div>
      ) : (
        <div>
          {/* Desktop View */}
          <div className="hidden md:block h-[54vh] overflow-y-auto">
            <GenericTable
              tableColumns={VGN_MATERIAL_COLUMNS}
              tableRows={materials}
              columnLabels={VGN_MATERIAL_LABELS}
              renderField={renderFields}
            />
          </div>

          {/* Mobile View */}
          <div className="block md:hidden h-[60vh] overflow-y-auto">
            {renderMobileCards()}
          </div>

          {/* Pagination */}
          <Pagination
            page={page}
            rowsPerPage={rowsPerPage}
            totalPages={totalPages}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
          />
        </div>
      )}
    </motion.div>
  );
};

export default VendorGoodsNoteList;
